import PropTypes from 'prop-types';
import React, { useState } from 'react';
import * as Yup from 'yup';
import { Icon } from '@iconify/react';
import { sentenceCase } from 'change-case';
import plusFill from '@iconify/icons-eva/plus-fill';
import arrowBackFilled from '@iconify/icons-eva/arrow-back-fill';
import { LinkasRouterLink, useNavigate, useParams } from 'react-router-dom';
// material
import {
  Card,
  Table,
  Stack,
  Avatar,
  Button,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Box,
  Autocomplete,
  TextField,
  Fade,
  Divider
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useFormik, Form, FormikProvider } from 'formik';
import { LoadingButton } from '@mui/lab';
// utils
import _ from 'lodash';
import { toast } from 'react-hot-toast';
import * as userService from '../services/user';

// components
import Page from '../components/Page';
import Label from '../components/Label';
import Scrollbar from '../components/Scrollbar';
import SearchNotFound from '../components/SearchNotFound';
import QuestionsGenerator from '../components/QuestionsGenerator';

const questions = [
  {
    id: 'username',
    label: 'Username',
    type: 'textinput',
    validation: { type: 'trim' },
    required: true
  },
  { id: 'name', label: 'Nama', type: 'textinput', validation: '', required: true },
  { id: 'nip', label: 'NIP', type: 'textinput', validation: '', required: true },
  { id: 'email', label: 'Email', type: 'textinput', validation: { type: 'email' }, required: true }
];

const questionsPassword = [
  {
    id: 'password_old',
    label: 'Password lama',
    type: 'password',
    required: true
  },
  {
    id: 'password_new',
    label: 'Password baru',
    type: 'password',
    required: true
  },
  {
    id: 'password_repeat',
    label: 'Ulangi password baru',
    type: 'password',
    required: true
  }
];

Profile.propTypes = {
  user: PropTypes.object.isRequired
};

export default function Profile({ user }) {
  const [instansis, setInstansis] = React.useState([]);
  const [instansi, setInstansi] = React.useState('');
  const userID = user?.attributes?.id;

  const theme = useTheme();
  const navigate = useNavigate();

  const questionsID = {};
  const questionsSchema = {};
  const valuesFromAPI = {};

  questions.forEach((question) => {
    questionsID[question.id] = '';
    if (!_.isEmpty(valuesFromAPI)) questionsID[question.id] = valuesFromAPI[question.id];

    if (question.required)
      questionsSchema[question.id] = Yup.string().required(`${question.label} wajib di-isi`);

    if (question?.validation?.type) {
      if (question?.validation?.type === 'trim')
        questionsSchema[question.id] = Yup.string()
          .trim()
          .required(`${question.label} wajib di-isi`);

      if (question?.validation?.type === 'email')
        questionsSchema[question.id] = Yup.string()
          .email()
          .required(`${question.label} wajib di-isi`);

      if (question?.validation?.type === 'password')
        questionsSchema[question.id] = Yup.string()
          .min(8)
          .required(`${question.label} wajib di-isi`);
    }
  });

  const QuestionSchema = Yup.object().shape(questionsSchema);

  const formik = useFormik({
    initialValues: {
      ...questionsID
    },
    enableReinitialize: true,
    validationSchema: QuestionSchema,
    onSubmit: () => {
      saveData();
    }
  });

  const {
    errors,
    touched,
    values,
    setValues,
    isSubmitting,
    handleSubmit,
    getFieldProps,
    setSubmitting,
    setFieldValue
  } = formik;

  const saveData = async () => {
    setValues({ ...values, username: values.username.replace(/\s+/g, '') });

    try {
      const body = { ...values, id_instansi: instansi.id, user_id: userID };

      if (!body.id_instansi) {
        toast.error('Gagal mendapatkan id instansi');
        return setSubmitting(false);
      }
      toast
        .promise(userService.addUser(body), {
          loading: 'Menyimpan data ...',
          error: <b>Maaf, terjadi kesalahan</b>,
          success: <b>Data berhasil disimpan</b>
        })
        .then(() => {});
    } catch (error) {
      toast.error(error.message);
    }
    setSubmitting(false);
  };

  const getInstansis = async () => {
    try {
      const result = await userService.getAll();
      const { data } = result;
      const filtered = data.filter((item) => item?.attributes?.company);

      const newInstansis = [];
      filtered.forEach((item) => {
        newInstansis.push({ id: item?.attributes?.id_instansi, label: item?.attributes?.company });
      });
      setInstansis(newInstansis);
    } catch (error) {
      toast.error(error.message);
    }
  };

  React.useEffect(() => {
    getInstansis();
  }, []);

  const getProfile = async () => {
    try {
      if (!userID) return;
      toast
        .promise(userService.getProfile({ id: userID }), {
          loading: 'Mengambil data ...',
          error: <b>Maaf, terjadi kesalahan</b>,
          success: <b>Data berhasil dimuat</b>
        })
        .then(({ data }) => {
          setValues({
            ...values,
            name: data?.name,
            nip: data?.nip,
            email: data?.email,
            username: data?.username,
            id_role: data?.id_role.toString(),
            id_instansi: data?.id_instansi
          });
          if (data?.id_instansi) setInstansi({ id: data.id_instansi, label: data?.Instansi?.name });
        });
    } catch (error) {
      toast.error(error.message);
    }
  };

  const changePassword = async () => {
    if (!values.password_old) return toast.error('Password lama belum diisi');
    if (!values.password_new) return toast.error('Password baru belum diisi');
    if (!values.password_repeat) return toast.error('Password konfirmasi belum diisi');

    if (values.password_new !== values.password_repeat)
      return toast.error('Password baru dan password konfirmasi tidak cocok');

    if (values.password_new.length < 8) return toast.error('Minimal password 8 karakter');
    if (values.password_repeat.length < 8) return toast.error('Minimal password 8 karakter');

    try {
      const body = {
        password_new: values.password_new,
        password_old: values.password_old,
        id: user?.attributes?.id
      };

      if (!body.id) return toast.error('User ID tidak ditemukan');

      toast
        .promise(userService.changePassword(body), {
          loading: 'Mengupdate password...',
          error: <b>Maaf, terjadi kesalahan, cek kembali password Anda</b>,
          success: <b>Password berhasil diubah</b>
        })
        .then(() => {
          setValues({ ...values, password_new: '', password_old: '', password_repeat: '' });
        });
    } catch (error) {
      toast.error(error.message);
    }
  };

  React.useEffect(() => {
    getProfile();
  }, [userID]);

  return (
    <Page title="User | Peladen Pemda DIY">
      <Container>
        <Stack direction="row" alignItems="center" spacing={2} mb={6}>
          <Typography variant="h4">Profile</Typography>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row', md: 'row', lg: 'row' }}
          spacing={2}
          divider={<Divider orientation="vertical" flexItem />}
        >
          <Stack direction="column" spacing={2} sx={{ flex: { lg: 6, xs: 12, sm: 12 } }}>
            <Typography variant="h6">Data Profil</Typography>
            <Autocomplete
              fullWidth
              disablePortal
              id="combo-box-instansi"
              options={instansis}
              value={instansi}
              onChange={(event, newValue) => {
                setInstansi(newValue);
              }}
              renderInput={(params) => (
                <TextField size="small" {...params} label="Pilih Instansi" />
              )}
            />

            <FormikProvider value={formik}>
              <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                <Fade in="true">
                  <Stack direction="column" spacing={2}>
                    {questions.map((item) => (
                      <QuestionsGenerator
                        item={item}
                        values={values}
                        setFieldValue={setFieldValue}
                        getFieldProps={getFieldProps}
                        errors={errors}
                        touched={touched}
                        isSubmitting={isSubmitting}
                        handleSubmit={handleSubmit}
                        setSubmitting={setSubmitting}
                        dataDukungs=""
                        setDataDukungs=""
                      />
                    ))}
                    <LoadingButton
                      mt={2}
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      loading={isSubmitting}
                    >
                      {userID ? 'Update Data' : 'Simpan Data'}
                    </LoadingButton>
                  </Stack>
                </Fade>
              </Form>
            </FormikProvider>
          </Stack>
          <Stack direction="column" spacing={2} sx={{ flex: { lg: 6, xs: 12, sm: 12 } }}>
            <Typography variant="h6">Password</Typography>
            <Fade in="true">
              <Stack direction="column" spacing={2}>
                {questionsPassword.map((item) => (
                  <QuestionsGenerator
                    item={item}
                    values={values}
                    setFieldValue={setFieldValue}
                    getFieldProps={getFieldProps}
                    errors={errors}
                    touched={touched}
                    isSubmitting={isSubmitting}
                    handleSubmit={handleSubmit}
                    setSubmitting={setSubmitting}
                    dataDukungs=""
                    setDataDukungs=""
                  />
                ))}
                <LoadingButton
                  mt={2}
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                  loading={isSubmitting}
                  onClick={() => changePassword()}
                >
                  Update Password
                </LoadingButton>
              </Stack>
            </Fade>
          </Stack>
        </Stack>
      </Container>
    </Page>
  );
}
